import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { get } from 'lodash';
import { AlertTriangle, Globe, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SiteScrapeUpgrade } from './SiteScrapeUpgrade';
import { UrlTree } from './UrlTree';
import {
  clearSelection,
  countSelected,
  mergeChildren,
  rootDomain,
  toggleSelection,
  trimToLimit,
  UrlTreeNode,
} from './selection';
import {
  clearSiteMapFlags,
  forgetSiteMap,
  isSiteMapEmpty,
  SITEMAP_CONSUMED_KEY,
  SITEMAP_RESUME_KEY,
} from './siteMapFlags';
import { QueryKey } from '@/Config/api/queryKey';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { useUploadStore } from '@/Domain/store/upload.store';
import { cn } from '@/Domain/utils/cn';
import {
  ONBOARDING_DISCOUNT_PERCENT_OFF,
  useOnboardingDiscount,
} from '@/UI/GettingStarted/components/onboardingDiscount';

type SiteMapStatus = 'none' | 'mapping' | 'ready' | 'failed';

interface SiteMapPreview {
  status: SiteMapStatus;
  total_urls: number;
  tree: UrlTreeNode | Record<string, never>;
  selection: string[];
  scrape_limit: number | null;
  articles_left: number | null;
  error_message: string | null;
  homepage_reject_reason: string | null;
}

// Backend `scrape_rejection_reason` verdict → advisory copy, mirroring
// LOW_QUALITY_COPY_KEY in the Getting Started flow. `reason` is untyped because
// it arrives straight off the site-map schema, so an unrecognised verdict falls
// back to the thin copy rather than rendering nothing.
const LOW_QUALITY_MODAL_KEY: Record<string, string> = {
  http_error: 'tabs.website.review.low_quality.blocked',
  error_page: 'tabs.website.review.low_quality.blocked',
  off_domain_redirect: 'tabs.website.review.low_quality.wrong_site',
  auth_wall: 'tabs.website.review.low_quality.auth_wall',
};

function lowQualityModalKey(reason: string): string {
  return LOW_QUALITY_MODAL_KEY[reason] ?? 'tabs.website.review.low_quality.thin';
}

const NO_LIMIT = Number.POSITIVE_INFINITY;

const SELECTION_CHANGED_DEBOUNCE_MS = 1200;

// Centered review modal. The URL is submitted elsewhere (the Add-website modal's
// All Pages tab, or onboarding); the dock opens this when the map is ready.
export const SiteScrapeReviewModal = () => {
  const { t } = useTranslation(['knowledge_base']);
  const queryClient = useQueryClient();
  const open = useModalStore((store) => store.isOpen(MODAL_NAMES.SITE_SCRAPE_REVIEW_MODAL_ID));
  const closeModal = useModalStore((store) => store.closeModal);
  const { state: discount, isClaimable: discountClaimable } = useOnboardingDiscount();
  const { getConfig } = useSubscription();
  // Treat "still loading" as paid: the safe default routes plan changes through
  // the subscription page instead of an instant checkout.
  const isPaidPlan = getConfig('tier') !== 'free';
  const addItems = useUploadStore((store) => store.addItems);
  const { capture } = usePostHogAnalytics();

  const [selection, setSelection] = useState<Set<string>>(new Set());
  // Seed the local selection from the server once per distinct ready map.
  const [syncedMapKey, setSyncedMapKey] = useState<string | null>(null);
  const openedAtRef = useRef<number | null>(null);
  const selectionTouchedRef = useRef(false);
  const submittedCountRef = useRef(0);
  const discardSnapshotRef = useRef({ selectedCount: 0, overLimit: false });
  // Defer review_opened until the open-time refetch settles so capacity is server-fresh, not the Infinity-cached preview.
  const openFetchStartedRef = useRef(false);
  const pendingReviewOpenRef = useRef<{ totalUrls: number; selectedCount: number } | null>(null);
  const seededServerSelectionRef = useRef<string | null>(null);

  const previewQuery = useQuery<SiteMapPreview>({
    queryKey: [QueryKey.knowledgeBase.siteMapPreview.get],
    enabled: open,
  });
  const preview = previewQuery.data;
  const status: SiteMapStatus = preview?.status ?? 'none';
  const tree = (
    preview?.tree && 'path' in preview.tree ? preview.tree : null
  ) as UrlTreeNode | null;
  // Empty map (dead/blocked domain, or a legacy ready+0 row): render the failure panel, not a dead-end picker.
  const mapEmpty = isSiteMapEmpty(status, preview?.total_urls ?? 0, preview?.error_message);

  const effectiveLimit = useMemo(() => {
    const limits = [preview?.scrape_limit, preview?.articles_left].filter(
      (limit): limit is number => typeof limit === 'number',
    );
    return limits.length ? Math.min(...limits) : NO_LIMIT;
  }, [preview?.scrape_limit, preview?.articles_left]);

  const selectedCount = tree ? countSelected(tree, selection) : 0;
  const overLimit = selectedCount > effectiveLimit;

  const dwellSeconds = () =>
    openedAtRef.current === null ? null : Math.round((Date.now() - openedAtRef.current) / 1000);

  // Emit a not-yet-fired review_opened so every terminal event has a matching open, even if the user acts before the refetch settles.
  const flushReviewOpened = useCallback(() => {
    const pending = pendingReviewOpenRef.current;
    if (!pending) return;
    pendingReviewOpenRef.current = null;
    capture(PostHogEventEnum.SiteScrapeReviewOpened, {
      total_urls: pending.totalUrls,
      selected_count: pending.selectedCount,
      articles_left: preview?.articles_left ?? null,
      over_limit: pending.selectedCount > effectiveLimit,
      quality_warning: Boolean(preview?.homepage_reject_reason) || pending.totalUrls <= 1,
      homepage_reject_reason: preview?.homepage_reject_reason ?? null,
    });
  }, [capture, preview?.articles_left, preview?.homepage_reject_reason, effectiveLimit]);

  const scrapeMutation = useMutation<unknown, Error, { selection: string[] }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.scrape],
    onSuccess: () => {
      toast.success(t('tabs.website.review.scrape_started'));
      flushReviewOpened();
      capture(PostHogEventEnum.SiteScrapeSubmitted, {
        selected_count: submittedCountRef.current,
        total_urls: preview?.total_urls ?? 0,
        articles_left: preview?.articles_left ?? null,
        dwell_seconds: dwellSeconds(),
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      // The backend consumed (deleted) the map; refresh so every surface goes idle.
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      queryClient.removeQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.children] });
      if (syncedMapKey) localStorage.setItem(SITEMAP_CONSUMED_KEY, syncedMapKey);
      // Seed the bottom-right progress widget so the batch tracks as a single
      // "Scraping X of N pages" bar (the backend meta article maps onto it).
      if (tree) addItems(rootDomain(tree), UploadItemTypeEnum.URL);
      // Drop the auto-open flags so the dock retires this now-handled map.
      clearSiteMapFlags();
      closeModal();
    },
    onError: (error) => {
      toast.error(
        get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed'),
      );
      // Capacity may have shifted under us (another tab/device); re-pull the truth.
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
    },
  });

  const selectionMutation = useMutation<unknown, Error, { selection: string[] }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.selection],
    // Sync the cached selection (tree untouched) so a resumed map seeds the persisted selection, not the pre-persist one.
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<SiteMapPreview>(
        [QueryKey.knowledgeBase.siteMapPreview.get],
        (old) => (old ? { ...old, selection: variables.selection } : old),
      );
    },
  });

  const discardMutation = useMutation<unknown, Error, void>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.delete],
    onSuccess: () => {
      forgetSiteMap();
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      queryClient.removeQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.children] });
      toast.success(t('tabs.website.review.discard_toast'));
      flushReviewOpened();
      capture(PostHogEventEnum.SiteScrapeDiscarded, {
        source: 'modal',
        selected_count: discardSnapshotRef.current.selectedCount,
        total_urls: preview?.total_urls ?? 0,
        articles_left: preview?.articles_left ?? null,
        over_limit: discardSnapshotRef.current.overLimit,
        dwell_seconds: dwellSeconds(),
      });
      closeModal();
    },
    onError: (error) => {
      toast.error(
        get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed'),
      );
    },
  });

  // Re-pull the preview whenever the modal opens: articles_left changes out-of-band
  // (uploads elsewhere, plan upgrades), and staleTime is Infinity app-wide.
  useEffect(() => {
    if (open) {
      openFetchStartedRef.current = false;
      pendingReviewOpenRef.current = null;
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.knowledgeBase.siteMapPreview.get],
      });
    }
  }, [open, queryClient]);

  useEffect(() => {
    // An empty map renders the failure panel; don't seed a selection or queue review_opened for it.
    if (!(open && status === 'ready' && tree) || (preview?.total_urls ?? 0) === 0) return;
    const key = `${tree.path}:${preview?.total_urls}`;
    const serverSelection = JSON.stringify(preview?.selection ?? []);
    const isNewMap = syncedMapKey !== key;
    // Same map but the server selection changed (resume after persist, another tab): restore it unless the user edited.
    const restoreFreshSelection =
      !isNewMap &&
      serverSelection !== seededServerSelectionRef.current &&
      !selectionTouchedRef.current;
    if (!isNewMap && !restoreFreshSelection) return;
    const saved = preview?.selection ?? [];
    // Default to everything selected (the root prefix) for a fresh map.
    const initial = new Set(saved.length ? saved : [tree.path]);
    setSelection(initial);
    setSyncedMapKey(key);
    seededServerSelectionRef.current = serverSelection;
    if (isNewMap) {
      openedAtRef.current = Date.now();
      selectionTouchedRef.current = false;
    }
    // Hold the initial selection; the capture effect below fills in server-fresh capacity once a fetch settles.
    pendingReviewOpenRef.current = {
      totalUrls: preview?.total_urls ?? 0,
      selectedCount: countSelected(tree, initial),
    };
  }, [open, status, tree, preview?.selection, preview?.total_urls, syncedMapKey]);

  // Fire review_opened once per open, after the open-time refetch settles, so capacity comes from the server not the cache.
  useEffect(() => {
    if (!open) return;
    if (previewQuery.isFetching) {
      openFetchStartedRef.current = true;
      return;
    }
    if (openFetchStartedRef.current) flushReviewOpened();
  }, [open, previewQuery.isFetching, flushReviewOpened]);

  useEffect(() => {
    if (!open) {
      setSelection(new Set());
      setSyncedMapKey(null);
      openedAtRef.current = null;
      selectionTouchedRef.current = false;
      openFetchStartedRef.current = false;
      pendingReviewOpenRef.current = null;
      seededServerSelectionRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !selectionTouchedRef.current || status !== 'ready' || !tree) return;
    const timer = setTimeout(() => {
      capture(PostHogEventEnum.SiteScrapeSelectionChanged, {
        total_urls: preview?.total_urls ?? 0,
        selected_count: selectedCount,
        articles_left: preview?.articles_left ?? null,
        over_limit: overLimit,
      });
    }, SELECTION_CHANGED_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    open,
    status,
    tree,
    selection,
    selectedCount,
    overLimit,
    preview?.total_urls,
    preview?.articles_left,
    capture,
  ]);

  // Meter is the KB article allowance: existing articles pre-fill the bar, the
  // selection stacks on top, and "remaining" is the unfilled track.
  const articleLimitRaw = getConfig('articles_limit');
  const articleLimit =
    typeof articleLimitRaw === 'number' && articleLimitRaw > 0 ? articleLimitRaw : null;
  const showCapacity = effectiveLimit !== NO_LIMIT && articleLimit !== null;
  const usedCount = articleLimit !== null ? Math.max(0, articleLimit - effectiveLimit) : 0;
  const projectedUsed = usedCount + selectedCount;
  const usedPct = articleLimit !== null ? Math.min(100, (usedCount / articleLimit) * 100) : 0;
  const selectedPct =
    articleLimit !== null ? Math.min(100, (selectedCount / articleLimit) * 100) : 0;
  const selectedSegmentPct = Math.min(selectedPct, 100 - usedPct);

  const busy = scrapeMutation.isPending || discardMutation.isPending;
  const canTrim = overLimit && effectiveLimit !== NO_LIMIT && effectiveLimit > 0;
  // aria-disabled, not disabled: a disabled button leaves the tab order, taking its reason with it.
  const scrapeBlocked = selectedCount === 0 || overLimit;
  const blockedReasonId = useId();

  const handleScrape = () => {
    if (selectedCount === 0 || overLimit || busy) return;
    submittedCountRef.current = selectedCount;
    scrapeMutation.mutate({ selection: Array.from(selection) });
  };

  const handleDeselectAll = () => {
    selectionTouchedRef.current = true;
    setSelection(clearSelection());
  };

  const handleTrimToPlan = () => {
    if (!tree || effectiveLimit === NO_LIMIT || effectiveLimit <= 0) return;
    selectionTouchedRef.current = true;
    setSelection(trimToLimit(tree, effectiveLimit, selection));
  };

  const handleDiscard = () => {
    if (discardMutation.isPending) return;
    discardSnapshotRef.current = { selectedCount, overLimit };
    discardMutation.mutate();
  };

  // Close without scraping or discarding (Cancel, Esc, overlay): the map stays
  // resumable from the dock, but the walk-away itself is a funnel signal.
  const closeWithoutAction = () => {
    flushReviewOpened();
    if (openedAtRef.current !== null) {
      capture(PostHogEventEnum.SiteScrapeReviewClosed, {
        total_urls: preview?.total_urls ?? 0,
        selected_count: selectedCount,
        articles_left: preview?.articles_left ?? null,
        over_limit: overLimit,
        dwell_seconds: dwellSeconds(),
      });
      openedAtRef.current = null;
    }
    closeModal();
  };

  // Persist the selection + flag a post-checkout resume before the Stripe redirect.
  const persistSelectionAndFlag = async () => {
    try {
      await selectionMutation.mutateAsync({ selection: Array.from(selection) });
    } catch {
      // best-effort: still flag the resume so the modal reopens after checkout
    }
    localStorage.setItem(SITEMAP_RESUME_KEY, '1');
  };

  // Paid plans upgrade via the subscription page; keep the selection + resume
  // flag so the dock re-offers this review once the plan change lands.
  const handleManagePlan = async () => {
    await persistSelectionAndFlag();
    closeModal();
  };

  const handleChildrenLoaded = (path: string, batch: UrlTreeNode[]) => {
    queryClient.setQueryData<SiteMapPreview>([QueryKey.knowledgeBase.siteMapPreview.get], (old) =>
      old && old.tree && 'path' in old.tree
        ? { ...old, tree: mergeChildren(old.tree as UrlTreeNode, path, batch) }
        : old,
    );
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) closeWithoutAction();
      }}
    >
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              />
            </DialogPrimitive.Overlay>
            {/* Plain centering layer; the card itself is the Radix Content. */}
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
              <DialogPrimitive.Content
                asChild
                forceMount
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                <motion.div
                  // Grows out of the bottom-right dock: starts small + offset
                  // toward the dock, settles into the centered card.
                  className="pointer-events-auto flex h-[88vh] max-h-[720px] w-full max-w-2xl flex-col gap-4 rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl focus:outline-none"
                  initial={{ opacity: 0, scale: 0.85, x: 80, y: 80 }}
                  animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92, x: 60, y: 60 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 26, mass: 0.9 }}
                  style={{ transformOrigin: 'bottom right' }}
                  data-testid="site-scrape-review-modal"
                >
                  <div className="flex flex-col gap-1">
                    <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold text-foreground">
                      <span className="flex size-7 items-center justify-center rounded-lg bg-muted">
                        <Globe className="size-4 text-foreground" />
                      </span>
                      {t('tabs.website.review.title')}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {t('tabs.website.review.description')}
                    </DialogPrimitive.Description>
                  </div>

                  {status === 'failed' || mapEmpty ? (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                      data-testid="site-scrape-review-modal-failed"
                    >
                      <AlertTriangle className="size-6 text-destructive" />
                      <span className="max-w-sm text-center text-sm">
                        {t(
                          mapEmpty
                            ? 'tabs.website.review.map_empty'
                            : 'tabs.website.review.map_failed',
                        )}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleDiscard}
                        disabled={busy}
                        data-testid="site-scrape-review-failed-discard"
                      >
                        {discardMutation.isPending ? (
                          <Spinner className="size-4" />
                        ) : (
                          t('tabs.website.review.dismiss')
                        )}
                      </Button>
                    </div>
                  ) : status !== 'ready' || !tree ? (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                      data-testid="site-scrape-review-modal-loading"
                    >
                      <Spinner className="size-6" />
                      <span className="text-sm">{t('tabs.website.review.mapping')}</span>
                    </div>
                  ) : (
                    <>
                      <div
                        className={cn(
                          'flex shrink-0 flex-col gap-2.5 rounded-xl px-4 py-3 transition-colors',
                          overLimit
                            ? 'border border-destructive/30 bg-destructive-muted/40'
                            : 'bg-muted',
                        )}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-foreground" data-testid="site-scrape-review-found">
                            {t('tabs.website.review.found', { count: preview?.total_urls ?? 0 })}
                          </span>
                          {showCapacity && (
                            <span
                              className={cn(
                                'tabular-nums text-muted-foreground',
                                overLimit && 'font-semibold text-foreground',
                              )}
                              data-testid="site-scrape-review-limit"
                            >
                              {t('tabs.website.review.articles_used', {
                                used: projectedUsed,
                                limit: articleLimit ?? 0,
                              })}
                            </span>
                          )}
                        </div>
                        {showCapacity && (
                          <div
                            className="relative h-2 w-full overflow-hidden rounded-full bg-muted-foreground/15"
                            data-testid="site-scrape-review-progress"
                          >
                            <div
                              className={cn(
                                'absolute inset-y-0 left-0',
                                overLimit ? 'bg-destructive' : 'bg-foreground',
                              )}
                              style={{ width: `${usedPct}%` }}
                            />
                            <div
                              className={cn(
                                'absolute inset-y-0 transition-all duration-300',
                                overLimit ? 'bg-destructive' : 'bg-foreground',
                              )}
                              style={{ left: `${usedPct}%`, width: `${selectedSegmentPct}%` }}
                            />
                          </div>
                        )}
                        <div
                          className={cn(
                            'flex flex-wrap items-center justify-between gap-2',
                            !overLimit && 'sr-only',
                          )}
                          data-testid="site-scrape-review-over-limit-note"
                        >
                          <p
                            role="status"
                            aria-live="polite"
                            className="text-sm font-medium text-foreground"
                            data-testid="site-scrape-review-over-limit-status"
                          >
                            {overLimit &&
                              t(
                                canTrim
                                  ? 'tabs.website.review.over_limit'
                                  : 'tabs.website.review.over_limit_no_capacity',
                              )}
                          </p>
                          {canTrim && (
                            <Button
                              variant="dark"
                              size="sm"
                              onClick={handleTrimToPlan}
                              disabled={busy}
                              data-testid="site-scrape-review-trim-to-plan"
                            >
                              {t('tabs.website.review.trim_to_plan', { limit: effectiveLimit })}
                            </Button>
                          )}
                        </div>
                      </div>

                      <div
                        className="flex shrink-0 flex-wrap items-center gap-2"
                        data-testid="site-scrape-review-bulk-actions"
                      >
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={handleDeselectAll}
                          disabled={selectedCount === 0 || busy}
                          data-testid="site-scrape-review-deselect-all"
                        >
                          {t('tabs.website.review.deselect_all')}
                        </Button>
                      </div>

                      <p
                        className="shrink-0 px-1 text-xs text-muted-foreground"
                        data-testid="site-scrape-review-unavailable-note"
                      >
                        {t('tabs.website.review.unavailable_note')}
                      </p>

                      {(preview?.homepage_reject_reason || (preview?.total_urls ?? 0) <= 1) && (
                        <Alert
                          variant="warning"
                          className="shrink-0"
                          data-testid="site-scrape-review-quality-warning"
                        >
                          <AlertTitle>{t('tabs.website.review.low_quality.title')}</AlertTitle>
                          <AlertDescription>
                            {preview?.homepage_reject_reason
                              ? t(lowQualityModalKey(preview.homepage_reject_reason))
                              : t('tabs.website.review.low_quality.few_pages')}
                          </AlertDescription>
                        </Alert>
                      )}

                      <div
                        className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto pl-1 pr-3"
                        data-testid="site-scrape-review-tree"
                      >
                        <UrlTree
                          node={tree}
                          selection={selection}
                          onToggle={(path) => {
                            selectionTouchedRef.current = true;
                            setSelection((prev) => toggleSelection(prev, path));
                          }}
                          onChildrenLoaded={handleChildrenLoaded}
                        />
                      </div>

                      {overLimit && (
                        <SiteScrapeUpgrade
                          dense
                          selectedCount={selectedCount}
                          usedCount={usedCount}
                          currentLimit={articleLimit}
                          planLimit={effectiveLimit === NO_LIMIT ? null : effectiveLimit}
                          isPaidPlan={isPaidPlan}
                          onBeforeCheckout={persistSelectionAndFlag}
                          onManagePlan={handleManagePlan}
                          discountApplied={discountClaimable && !isPaidPlan}
                          discountPercentOff={ONBOARDING_DISCOUNT_PERCENT_OFF}
                          promoCode={discountClaimable ? discount?.code : undefined}
                        />
                      )}

                      <div className="flex w-full items-center justify-between gap-3 border-t border-border pt-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDiscard}
                          disabled={busy}
                          className="gap-1.5 px-2 text-muted-foreground hover:text-destructive"
                          data-testid="site-scrape-review-discard-button"
                        >
                          {discardMutation.isPending ? (
                            <Spinner className="size-4" />
                          ) : (
                            <Trash2 className="size-4" />
                          )}
                          {t('tabs.website.review.discard')}
                        </Button>
                        <div className="ml-auto flex items-center gap-3">
                          <span
                            id={blockedReasonId}
                            className={cn(
                              'sr-only text-sm sm:not-sr-only',
                              overLimit ? 'text-foreground' : 'text-muted-foreground',
                            )}
                            data-testid="site-scrape-review-status"
                          >
                            {overLimit
                              ? t('tabs.website.review.over_limit_by', {
                                  count: selectedCount - effectiveLimit,
                                })
                              : t('tabs.website.review.selected', { count: selectedCount })}
                          </span>
                          <Button
                            variant="secondary"
                            onClick={closeWithoutAction}
                            disabled={busy}
                            className="shrink-0"
                            data-testid="site-scrape-review-cancel-button"
                          >
                            {t('tabs.website.review.cancel')}
                          </Button>
                          <Button
                            variant="dark"
                            onClick={handleScrape}
                            disabled={busy}
                            aria-disabled={scrapeBlocked || undefined}
                            aria-describedby={scrapeBlocked ? blockedReasonId : undefined}
                            className="shrink-0 aria-disabled:pointer-events-none aria-disabled:bg-foreground/20 aria-disabled:text-background/80 aria-disabled:opacity-50 aria-disabled:focus-visible:bg-foreground/20"
                            data-testid="site-scrape-review-scrape-button"
                          >
                            {scrapeMutation.isPending && <Spinner className="size-4" />}
                            {t('tabs.website.review.scrape_button', { count: selectedCount })}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
};
